// ISDP CUSTOM — i18n "devsecops" namespace (the DevSecOps / Self Service hub). See ./common.ts for the
// convention. English is the source of truth (nested; keys flatten with dots); German lazy-imported.
import { createTranslationRef } from '@backstage/core-plugin-api/alpha';

export const devsecopsMessages = createTranslationRef({
  id: 'devhub.devsecops',
  messages: {
    // Hub shell (banner + left rail)
    shell: {
      eyebrow: 'Dräger DevHub',
      title: 'Self Service',
      intro:
        'Spin up new projects, manage your existing ones, and reach the tools your team needs — all in one place.',
      adminOnlyTitle: 'Administrators only',
      adminOnlyBody:
        'This area of Self Service is available to DevHub administrators. Head back to your projects, or ask an admin if you need access.',
      backToProjects: 'Back to My Projects',
    },
    // HUB_SECTIONS labels (rail)
    sections: {
      create: 'Create',
      projects: 'My Projects',
      users: 'Users & Permissions',
      apikeys: 'API Keys',
      idfinder: 'Id Finder',
      usage: 'Modules Usage',
      pageinfo: 'Modules Page Info',
      guide: 'Guide',
    },
    // MODULE_TABS labels (per-tool tab strip)
    moduleTabs: {
      users: 'User Management',
      config: 'Config',
      configuration: 'Configuration',
      docs: 'Documentation',
      orders: 'Orders',
      files: 'Files',
      overview: 'Repositories',
      secrets: 'Secrets',
      info: 'Info',
    },
    // Shared bits
    common: {
      loading: 'Loading…',
      none: 'None.',
      any: 'Any',
      all: 'All',
      done: 'Done',
      apiDocs: 'API Docs',
      openDocumentation: 'Open documentation',
    },
    // DsMultiSelect
    multiSelect: {
      selected: '{{num}} selected',
      noOptions: 'No options.',
    },
    // Create catalog (SelfServiceCreate)
    create: {
      heading: 'Create',
      intro:
        'Everything you can spin up in one place — documentation, DevSecOps tools for your projects, and new components from templates.',
      searchPlaceholder: 'Search tools and templates…',
      searchAria: 'Search tools and templates',
      loadingTemplates: 'Loading templates…',
      noMatch: 'Nothing matches your search.',
      // Documentation category (spec.type: documentation), starter kit featured first
      docsLabel: 'Documentation',
      docsHint: 'Write docs that publish straight to the DevHub.',
      docsEmpty: 'No documentation templates available yet.',
      featuredBadge: 'Recommended',
      featuredPitch:
        'Get your own docs repository in Azure DevOps with a starter site, a live preview and DevHub publishing already wired up. Takes about two minutes.',
      featuredCta: 'Start',
      // DevSecOps tools category (module info cards)
      toolsLabel: 'DevSecOps tools',
      toolsHint:
        "These live inside a project. Open or create a project to add one — the card just shows what's available.",
      noModules: 'No modules match your search.',
      moduleFallbackType: 'DevSecOps module',
      addedInside: 'Added inside a project → My Projects',
      addedInsideTitle: 'Added inside a project — go to My Projects',
      goToProjectsTitle: 'Add this inside a project',
      goToProjectsBody:
        'Tools like this are added from a project’s "Add module" screen, not from here. We’ll take you to My Projects so you can open an existing project or create one.',
      goToProjectsConfirm: 'Go to My Projects',
      goToProjectsCancel: 'Stay here',
      toolsBlocked: 'Your account is blocked from the DevSecOps Hub. Contact the Hub admins for access.',
      // Any other templates, one section per spec.type
      typeGroupLabel: '{{type}} templates',
      otherGroupLabel: 'Other templates',
      templateFallbackDesc: 'Scaffolder template',
    },
    // Custom scaffolder fields (AuthorProfile, AdoRepoTarget)
    fields: {
      authorName: 'Name',
      authorEmail: 'Email',
      authorHint: 'Shown as the author of your docs. Prefilled from your Dräger profile — change it if needed.',
      authorNameRequired: 'Enter the author name.',
      authorEmailRequired: 'Enter the author email.',
      authorEmailInvalid: 'Enter a valid email address.',
      adoProject: 'Azure DevOps project',
      adoProjectPlaceholder: 'Type to search your projects…',
      adoOpen: 'Show projects',
      adoClose: 'Hide projects',
      adoClear: 'Clear',
      adoNeedPat: 'Enter your Azure DevOps personal access token above to load your projects.',
      adoLoadingProjects: 'Loading your Azure DevOps projects…',
      adoErrUnauthorized:
        'Azure DevOps rejected the token. Check that it is valid, not expired, and has the "Project & Team (Read)" scope.',
      adoErrNetwork: 'Could not reach Azure DevOps. Check your connection (VPN) and try again.',
      adoErrHttp: 'Azure DevOps answered with HTTP {{status}}. Try again in a moment.',
      adoNoProjects: 'This token cannot see any Azure DevOps projects.',
      adoNewToAdo: 'New to Azure DevOps?',
      adoOrderLicense: 'Order a license',
      adoRequestProject: 'request a project',
      adoNoFilterMatch: 'No projects match your filter.',
      adoProjectCount: '{{num}} projects — type to filter',
      adoRetry: 'Retry',
      repoName: 'Repository name',
      repoHint: 'Lower-case words joined by single hyphens. “‑docs” is added for you. Also becomes the DevHub catalog name.',
      repoWillBe: 'Your repository will be called',
      repoChecking: 'Checking availability…',
      repoAvailable: 'Available',
      repoTakenAdo: 'A repository with this name already exists in this project.',
      repoTakenCatalog: 'This name is already used by a DevHub catalog entry — pick another so the two do not clash.',
      repoCheckFailed: 'Could not check whether the name is free.',
      projectRequired: 'Select an Azure DevOps project.',
      repoRequired: 'Enter a repository name.',
      repoPattern: 'Use lower-case letters and digits, words joined by single hyphens (no hyphen at the start or end), up to 63 characters.',
      docTypeQuickTitle: 'Quick page',
      docTypeQuickDesc: 'One topic on one page.',
      docTypeQuickBest: 'a how-to, a short explanation, an entry for a tool',
      docTypeGuideTitle: 'Guide with sections',
      docTypeGuideDesc: 'A manual with chapters, diagrams and zoomable screenshots.',
      docTypeGuideBest: 'handbooks, onboarding, product and team manuals',
      docTypeBilingualTitle: 'Bilingual guide (EN/DE)',
      docTypeBilingualDesc: 'The same guide in English and German; readers switch language.',
      docTypeBilingualBest: 'docs read by teams in Germany and abroad',
      docTypeBestFor: 'Best for:',
      docTypeRequired: 'Pick the kind of documentation.',
      repoStillChecking: 'Still checking whether the name is free — try again in a moment.',
    },
    // Doc Author Starter Kit: "doc ready" page after a run (DocReadyPage)
    docReady: {
      title: 'Your draft doc is on its way',
      intro:
        'The DevHub is picking up {{name}}. This usually takes about a minute — we open the doc as soon as it is there.',
      stepRepo: 'Repository created',
      stepCatalog: 'Adding it to the DevHub …',
      stepOpen: 'Opening your draft',
      timeoutTitle: 'This is taking longer than usual',
      timeoutBody:
        'Your repository is created; the DevHub just has not listed it yet. That can take a few more minutes. Try again in a moment, or open the repository in the meantime.',
      tryAgain: 'Try again',
      openRepo: 'Open the repository',
      invalidTitle: 'This link is incomplete',
      invalidBody: 'It does not name a doc we can open. Start from Create to make a new one.',
      backToCreate: 'Go to Create',
    },
    // All Projects (My Projects)
    projects: {
      heading: 'My Projects',
      addNew: 'Add new project',
      searchPlaceholder: 'Search projects…',
      filterByModule: 'Filter by module',
      clearModules: 'Clear modules',
      viewGrid: 'Grid',
      viewList: 'List',
      count: '{{num}} projects',
      filtering: 'Filtering…',
      noMatch: 'No projects match.',
      noDescription: 'No description.',
      unknownProduct: 'Unknown',
      colName: 'Name',
      colProduct: 'Product',
      colCostCenter: 'Cost center',
      colDepartment: 'Department',
      colContacts: 'Contacts',
      atLeastOneContact: 'At least one contact is required.',
      newProject: 'New project',
      fieldName: 'Project name',
      fieldDescription: 'Description',
      fieldProduct: 'Product',
      fieldCostCenter: 'Cost center',
      fieldDepartment: 'Department',
      fieldOrderNumber: 'Internal order number',
      optional: '(optional)',
      contacts: 'Contacts',
      contactEmail: 'email',
      contactJob: 'job',
      removeContact: 'Remove contact',
      addContactHint: 'Add at least one contact.',
      creating: 'Creating…',
      createProject: 'Create project',
    },
    // Hub Users & Permissions
    hubUsers: {
      heading: 'Users & Permissions',
      addUser: 'Add user',
      subtitle:
        'Global Hub roles. Project- and module-scoped roles are managed inside each project.',
      searchPlaceholder: 'Search users…',
      allProjects: 'All projects',
      filterByProject: 'Filter by project',
      clearProject: 'Clear project',
      count: '{{num}} users',
      colUser: 'User',
      colGlobalRole: 'Global role',
      colProjectRoles: 'Project roles',
      colLastLogin: 'Last login',
      editRole: 'Edit role',
      remove: 'Remove',
      removeUserTitle: 'Remove this user?',
      removeUserMessagePrefix: ' will lose their global DevSecOps Hub roles.',
      editUser: 'Edit {{id}}',
      userEmail: 'User email',
      globalRole: 'Global role',
      saving: 'Saving…',
    },
    // API Keys (hub + project share many strings)
    apikeys: {
      heading: 'API Keys',
      create: 'Create API key',
      hubSubtitle: 'All project API keys across the Hub.',
      projectEmpty: 'No API keys for this project.',
      hubEmpty: 'No API keys.',
      colName: 'Name',
      colProject: 'Project',
      colPrimary: 'Primary key',
      colSecondary: 'Secondary key',
      colValidUntil: 'Valid until',
      colLastUsed: 'Last used',
      deleteTitle: 'Delete this API key?',
      deleteHubMessage: 'The key will stop working immediately. This cannot be undone.',
      deleteProjectMessageSuffix:
        ' will stop working immediately. Any integration using it will lose access. This cannot be undone.',
      edit: 'Edit',
      delete: 'Delete',
      createdNote: 'Key created. Copy these now — the primary key is only shown once.',
      copyOnceNote: 'Copy these now — the secret values are shown only once.',
      fieldPrimary: 'Primary key',
      fieldSecondary: 'Secondary key',
      fieldProject: 'Project',
      selectProject: 'Select project…',
      fieldName: 'Name',
      fieldNamePlaceholder: 'e.g. Integration key for my service',
      fieldExpiration: 'Expiration',
      fieldRole: 'Role',
      fieldAccess: 'Access',
      tenantSystem: 'Tenant system',
      customRole: 'Custom role',
      editTitle: 'Edit API key',
      tenantScopeNote:
        'This key uses a tenant-system scope map; delete and recreate it to change its access.',
      creating: 'Creating…',
      saving: 'Saving…',
    },
    // Id Finder
    idfinder: {
      heading: 'Id Finder',
      intro:
        'Look up a project or module by name, project id, module id, or linked (external tool) id.',
      indexing: ' · Indexing…',
      searchPlaceholder: 'project / module / linked id or name…',
      colMatchedBy: 'Matched by',
      colProject: 'Project',
      colModule: 'Module',
      colProjectId: 'Project id',
      colModuleId: 'Module id',
      colLinkedId: 'Linked id',
      noMatches: 'No matches.',
      matchProjectName: 'Project name',
      matchProjectId: 'Project id',
      matchModuleName: 'Module name',
      matchModuleId: 'Module id',
      matchLinkedId: 'Linked id',
    },
    // Modules Usage
    usage: {
      heading: 'Modules Usage',
      intro:
        'How many instances of each module type exist and how many projects use them.',
      computing: 'Computing usage…',
      colModule: 'Module',
      colType: 'Type',
      colHosting: 'Hosting',
      colInstances: 'Instances',
      colProjects: 'Projects',
      externalConsole: 'External console',
      inHub: 'In-Hub',
      copyContacts: 'Copy contacts',
      noContacts: 'No contacts',
      copied: 'Copied',
    },
    // Modules Page Info
    pageinfo: {
      heading: 'Modules Page Info',
      intro:
        'The in-Hub pages (tabs) each module type registers, and its default page.',
      colModule: 'Module',
      colModuleId: 'Module id',
      colPages: 'Pages',
      colDefaultPage: 'Default page',
      externalConsoleDash: '— (external console)',
    },
    // Guide (hub)
    guide: {
      heading: 'Guide',
      introPrefix: 'is where Dräger teams provision what they need — without waiting on manual setup. There are two things you can do here:',
      addToolsTitle: 'Add tools to a project',
      addToolsBody1: 'Open',
      addToolsBody2:
        ', pick a project, and attach tool modules — IDMS, DPAS, DOOP, GitHub, SonarQube and more. Access is governed by per-project and per-module roles.',
      createNewTitle: 'Create something new',
      createNewBody1: 'Use',
      createNewBody2:
        'to scaffold a new repository, docs site, or service from an approved template — production-ready in minutes.',
      quickStart: 'Quick start',
      step1: 'under My Projects. Don\'t have one? Ask an admin to create it for your team.',
      step1Bold: 'Find your project',
      step2: 'from the project\'s Modules tab and follow its setup.',
      step2Bold: 'Add a module',
      step3Bold: 'Read the docs',
      step3a: '— every module has a guide in',
      step4Bold: 'Manage access',
      step4: '— admins grant per-project / per-module roles and issue API keys for automation.',
      needHelp: 'Need help?',
      emailPrefix: 'Email',
      emailSuffix: 'or reach the platform team',
      copyTeam: 'Copy team name',
      myProjects: 'My Projects',
      createLink: 'Create',
      devhubDocs: 'DevHub Docs',
      docsLink: 'Docs',
    },
    // Module instance page
    moduleInstance: {
      loading: 'Loading…',
      notFound: 'This module is not available in this project — it may have been removed or you may not have access.',
      notFoundTitle: 'Module not available',
      backToProject: 'Back to project',
      type: 'Type',
      group: 'Group',
      externalLinks: 'links to an external resource.',
      open: 'Open {{name}}',
      externalConsolePrefix: 'is managed in its own console for linked id',
      externalConsoleSuffix: '. It has no in-Hub data view.',
    },
    // Project detail page
    project: {
      allProjects: 'All projects',
      notFoundTitle: 'Project not found',
      notFoundBody:
        "We couldn't open this project — it may have been removed, or you may not have access to it.",
      product: 'Product',
      costCenter: 'Cost center',
      department: 'Department',
      modules: 'Modules',
      loading: 'Loading project…',
      tabModules: 'Modules',
      tabPermissions: 'Users & Permissions',
      tabApiKeys: 'API Keys',
      tabInfo: 'Project Information',
      tabHelp: 'Help',
      toastModuleAdded: 'Module added.',
      toastModuleUpdated: 'Module updated.',
      toastModuleRemoved: 'Module removed.',
      toastPermissionsSaved: 'Permissions saved.',
      toastApiKeyUpdated: 'API key updated.',
      toastApiKeyDeleted: 'API key deleted.',
      toastProjectSaved: 'Project saved.',
      toastProjectDeleted: 'Project deleted.',
    },
    // Modules tab (project)
    modulesTab: {
      heading: 'Modules',
      addModule: 'Add module',
      empty: 'No modules attached to this project.',
      editRename: 'Edit / rename',
      remove: 'Remove',
      step2Prefix: 'Step 2 — configure',
      step1: 'Step 1 — pick a module type to attach to this project.',
      moduleType: 'Module type',
      selectModule: 'Select a module…',
      displayName: 'Display name',
      linkedId: 'Linked ID',
      linkedRequiredPrefix: '(required — the project/app id in',
      linkedRequiredSuffix: ')',
      linkedOptional: '(optional)',
      group: 'Group',
      migrate: 'Migrate existing resource (link instead of provision)',
      removeModuleTitle: 'Remove module?',
      removeModuleQuestionPrefix: 'Remove',
      removeModuleQuestionSuffix: 'from this project?',
      softRemove: 'Soft remove',
      recommended: '(recommended)',
      softRemoveDesc: 'Detach the module; underlying resource is preserved.',
      hardRemove: 'Hard remove',
      hardRemoveDesc: 'Permanently delete the module instance and its references.',
      softRemoveAria: 'Soft remove (recommended)',
      hardRemoveAria: 'Hard remove',
      editModule: 'Edit module',
      typeLinkedImmutable: "Type and linked id can't be changed after creation.",
    },
    // Permissions tab
    permissions: {
      heading: 'Users & Permissions',
      addUser: 'Add user',
      empty: 'No users have roles on this project yet.',
      colUser: 'User',
      colProjectRole: 'Project role',
      colModuleGrants: 'Module-level grants',
      editRoles: 'Edit roles',
      addUserTitle: 'Add user',
      editRolesTitle: 'Edit roles — {{who}}',
      userEmail: 'User email',
      invalidEmail: 'Not a valid email address.',
      project: 'Project',
      projectRole: 'Project role',
      noAccess: 'No access',
      perModule: 'Per module',
      noModules: 'This project has no modules.',
      inheritNone: 'Inherit / none',
      endpointCount: '{{num}} endpoint',
      endpointCountPlural: '{{num}} endpoints',
      expand: 'Expand',
      collapse: 'Collapse',
      saveRoles: 'Save roles',
    },
    // API Keys tab (project extras beyond apikeys.*)
    apikeysTab: {
      addProjectRole: 'Project role',
    },
    // Project Information tab
    info: {
      heading: 'Project Information',
      projectIcon: 'Project icon',
      uploadImage: 'Upload image',
      removeImage: 'Remove image',
      iconTooLarge: 'Image too large — must be 64KB or smaller.',
      iconReadError: 'Could not read that file.',
      displayName: 'Display name',
      description: 'Description',
      product: 'Product',
      department: 'Department',
      costCenter: 'Cost center',
      orderNumber: 'Internal order number',
      contacts: 'Contacts',
      noContacts: 'No contacts yet.',
      removeContact: 'Remove contact',
      email: 'Email',
      job: 'Job',
      jobPlaceholder: 'Owner',
      addEmail: 'Add email',
      deleteProject: 'Delete project',
      saveChanges: 'Save changes',
      deleteProjectTitle: 'Delete project?',
      deleteQuestionPrefix: 'Delete',
      deleteQuestionSuffix: 'from the Hub mirror?',
      softDelete: 'Soft delete',
      recommended: '(recommended)',
      softDeleteDesc:
        'Archive the project; module references are removed but nothing is deleted in any external tool.',
      hardDelete: 'Hard delete',
      hardDeleteDesc: 'Permanently remove the project and all its references.',
      softDeleteAria: 'Soft delete (recommended)',
      hardDeleteAria: 'Hard delete',
    },
    // Help tab (project)
    help: {
      heading: 'Help',
      intro: 'This project is organised into the following sections:',
      modulesLabel: 'Modules',
      modulesDesc:
        'Attach and configure tool modules (GitHub, SonarQube, …) on this project.',
      usersLabel: 'Users & Permissions',
      usersDesc: 'Grant project- and module-scoped roles to users.',
      apikeysLabel: 'API Keys',
      apikeysDesc: 'Create, rotate, and revoke API keys scoped to this project.',
      infoLabel: 'Project Information',
      infoDesc: 'Edit metadata, icon, contacts, and archive or delete the project.',
    },
    // ModuleTabContent — shared / generic
    tab: {
      loadingNamed: 'Loading {{name}}…',
      noDataView: 'No data view for this tab.',
      couldntLoadPrefix: "Couldn't load",
      couldntLoadSuffix: 'from the tool:',
      noData: 'No data.',
    },
    // Module docs stub
    moduleDocs: {
      body: 'The full documentation for this module type is published in the DevHub docs.',
      open: 'Open documentation',
    },
    // IDMS users
    idmsUsers: {
      filterPlaceholder: 'Filter users…',
      filterByRole: 'Filter by role',
      allRoles: 'All roles',
      count: '{{num}} users',
      addUser: 'Add user',
      colUser: 'User',
      colEmail: 'Email',
      colRoleScopes: 'Role / Scopes',
      colLastLogin: 'Last login',
      edit: 'Edit',
      delete: 'Delete',
      removeTitle: 'Remove this user?',
      removeMessageSuffix: ' will lose access to this application. This writes to the live IDMS.',
      savedRolesFor: 'Saved roles for {{id}}.',
      removed: 'Removed {{id}}.',
    },
    // IDMS user editor
    idmsUserEditor: {
      addUser: 'Add user',
      editUser: 'Edit {{id}}',
      userIdLabel: 'User (email or id)',
      baseRole: 'Base role',
      app: 'App',
      subscopes: 'Subscopes',
      noSubscopes: 'No subscopes — the base role applies everywhere.',
      removeSubscope: 'Remove subscope',
      subscopePlaceholder: 'subscope name (e.g. global)',
      saving: 'Saving…',
    },
    // IDMS config
    idmsConfig: {
      saved: 'Configuration saved.',
      appDisplayName: 'App display name',
      clientId: 'Client ID',
      readOnly: '(read-only)',
      authLevels: 'Auth levels',
      addAuthLevel: 'Add auth level…',
      addLevel: 'Add level',
      defaultAuthLevel: 'Default auth level',
      noneParen: '(none)',
      userTenant: 'User tenant',
      tenantDisabledHint: 'Tenant management is disabled — users are not assigned a tenant.',
      authRulesLabel: 'Default auth rules by email',
      advancedOptional: '(advanced, optional)',
      authRulesHelp1: 'Assign a sign-up auth level by email pattern. One rule per line, format',
      authRulesHelp2: 'The first matching rule wins; if none match, the default auth level above is used.',
      invalidRule: 'Invalid rule: {{msg}}',
      rulesValid: 'Rules valid',
      authRulesInvalidJson: 'Default auth rules contain invalid JSON — fix or clear the field.',
      disableTenant: 'Disable tenant management',
      notificationMails: 'Notification mails',
      addNotification: 'Add notification',
      notificationHint:
        'A mail is sent when a user\'s auth level changes from → to (use "Any" to match any level).',
      from: 'From',
      to: 'To',
      fromAria: 'From auth level',
      toAria: 'To auth level',
      key: 'key:',
      composeMail: 'Compose mail',
      edited: 'edited',
      edit: 'Edit',
      delete: 'Delete',
      noMails: 'No notification mails configured yet.',
      otherConfig: 'Other configuration',
      addConfig: 'Add configuration',
      colKey: 'Key',
      colValue: 'Value',
      noOtherConfig: 'No other configuration.',
      saving: 'Saving…',
      saveChanges: 'Save changes',
    },
    // IDMS kv editor
    idmsKv: {
      addConfig: 'Add configuration',
      editKey: 'Edit {{key}}',
      key: 'Key',
      keyPlaceholder: 'ConfigKey',
      value: 'Value',
    },
    // IDMS mail editor
    idmsMail: {
      mailReceivers: 'Mail receivers',
      commaSeparated: '(comma-separated)',
      invalidEmail: 'Invalid email: {{list}}',
      subject: 'Subject',
      senderMail: 'Sender mail',
      notValidEmail: 'Not a valid email address.',
      templateId: 'Template ID',
      mailContent: 'Mail content',
      templateData: 'Template data',
      json: '(JSON)',
      templateDataError: 'Template data must be valid JSON.',
      apply: 'Apply',
    },
    // DOOP orders
    doopOrders: {
      filterPlaceholder: 'Filter orders by name…',
      searchByDate: 'Search by date field',
      lastModified: 'Last Modified',
      acceptanceDate: 'Acceptance Date',
      allStates: 'All states',
      allAreas: 'All areas',
      from: 'From',
      to: 'To',
      reset: 'Reset',
      count: '{{num}} orders',
      colName: 'Name',
      colUserStory: 'User Story',
      colEstimate: 'Estimate',
      colPriceModel: 'Price Model',
      colOrderNumber: 'Order #',
      colOrderNumberCsv: 'Order Number',
      colArea: 'Area',
      colState: 'State',
      colAcceptance: 'Acceptance',
      colLastModified: 'Last Modified',
      colLogs: 'Logs',
      logs: 'Logs',
      noOrders: 'No orders.',
      sumShown: 'Sum of shown: {{hours}} h',
    },
    // DOOP order logs
    doopLogs: {
      title: 'Logs — {{name}}',
      noEntries: 'No log entries.',
      colLevel: 'Level',
      colTime: 'Time',
      colMessage: 'Message',
    },
    // DOOP states
    doopStates: {
      New: 'New',
      'Estimate and Quote': 'Estimate and Quote',
      'Quote Acceptance': 'Quote Acceptance',
      Order: 'Order',
      Ordered: 'Ordered',
      'Request for Acceptance': 'Request for Acceptance',
      Done: 'Done',
    },
    // DOOP users
    doopUsers: {
      count: '{{num}} users',
      addUser: 'Add user',
      colName: 'Name',
      colEmail: 'Email',
      colAreas: 'Areas',
      colPoPm: 'PO/PM',
      colServiceMgr: 'Service Mgr',
      colContractor: 'Contractor',
      colExtServiceMgr: 'Ext.Service Mgr',
      all: 'All',
      edit: 'Edit',
      remove: 'Remove',
      removeTitle: 'Remove this DOOP user?',
      removeMessageSuffix:
        ' will be removed from this DOOP project. This writes to the live DOOP backend.',
      added: 'Added {{email}}.',
      saved: 'Saved {{email}}.',
      removed: 'Removed {{mail}}.',
      addTitle: 'Add DOOP user',
      name: 'Name',
      email: 'Email',
      areas: 'Areas',
      noneIsAll: '(none = all)',
      allAreas: 'All areas',
      saving: 'Saving…',
      add: 'Add',
      editTitle: 'Edit {{email}}',
      poPmLabel: 'PO/PM (can initiate process)',
      serviceMgrLabel: 'Service Manager (can accept orders)',
      contractorLabel: 'Contractor',
      extServiceMgrLabel: 'Ext. Service Manager (order informed)',
    },
    // DOOP config
    doopConfig: {
      saved: 'Configuration saved.',
      name: 'Name',
      defaultPriceModel: 'Default price model',
      defaultOrderNumber: 'Default order number',
      doopAreas: 'DOOP areas',
      allAreas: 'All areas',
      all: 'All',
      contractors: 'Contractors',
      addContractorPlaceholder: 'Add contractor…',
      addContractor: 'Add contractor',
      saving: 'Saving…',
      saveChanges: 'Save changes',
    },
    // GitHub repos
    githubRepos: {
      filterPlaceholder: 'Filter repositories…',
      count: '{{num}} repositories',
      addRepository: 'Add repository',
      colName: 'Name',
      colVisibility: 'Visibility',
      colUpdated: 'Updated',
      colLinkedSecrets: 'Linked secrets',
      private: 'Private',
      internal: 'Internal',
      openRepo: 'Open repo',
      edit: 'Edit',
      delete: 'Delete',
      deleteTitle: 'Delete this repository?',
      deleteMessagePrefix: 'Repository',
      deleteMessageSuffix: 'will be deleted from the GitHub org. This cannot be undone.',
      created: 'Created {{name}}.',
      deleted: 'Deleted {{name}}.',
    },
    // GitHub repo secrets editor
    githubRepoSecrets: {
      title: 'Secrets for {{name}}',
      linkedSecrets: 'Linked secrets',
      linkUnlink: '(link/unlink this repo)',
      noSecretsLinked: 'No secrets linked',
      saving: 'Saving…',
    },
    // GitHub repo editor
    githubRepoEditor: {
      addRepository: 'Add repository',
      repoName: 'Repository name',
      suffixPlaceholder: 'my-service',
      nameHint: '3–24 chars: letters, digits,',
      visibility: 'Visibility',
      internal: 'Internal',
      private: 'Private',
      team: 'Team',
      noTeams: '(no teams)',
      linkSecrets: 'Link secrets',
      optional: '(optional)',
      noSecrets: 'No secrets',
      creating: 'Creating…',
    },
    // GitHub secrets
    githubSecrets: {
      filterPlaceholder: 'Filter secrets…',
      count: '{{num}} secrets',
      addSecret: 'Add secret',
      colName: 'Name',
      colLinkedRepos: 'Linked repositories',
      colUpdated: 'Updated',
      all: 'All',
      edit: 'Edit',
      delete: 'Delete',
      deleteTitle: 'Delete this secret?',
      deleteMessagePrefix: 'Secret',
      deleteMessageSuffix: 'will be deleted from all linked repositories. This cannot be undone.',
      deleted: 'Deleted secret {{name}}.',
    },
    // GitHub secret editor
    githubSecretEditor: {
      addSecret: 'Add secret',
      editSecret: 'Edit {{name}}',
      secretName: 'Secret name',
      secretNamePlaceholder: 'MY_SECRET',
      value: 'Value',
      writeOnly: '(write-only; re-enter to update)',
      valuePlaceholder: 'secret value',
      scopeToRepos: 'Scope to repositories',
      noneIsAll: '(none = all repos)',
      allRepositories: 'All repositories',
      saving: 'Saving…',
    },
    // GitHub info
    githubInfo: {
      organization: 'Organization',
      slug: 'Slug',
      costCenter: 'Cost center',
    },
    // DPAS files
    dpasFiles: {
      images: 'Images',
      documents: 'Documents',
      secrets: 'Secrets',
      root: 'root',
      filterPlaceholder: 'Filter files…',
      countFiles: '{{num}} files',
      list: 'List',
      grid: 'Grid',
      downloadCsv: 'Download list (CSV)',
      uploadToRoot: 'Upload to root',
      uploadToPath: 'Upload to /{{path}}',
      upload: 'Upload',
      loadingFiles: 'Loading files…',
      noFiles: 'No files.',
      colName: 'Name',
      delete: 'Delete',
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page {{page}} of {{total}}',
      deleteTitle: 'Delete this file?',
      deleteMessageSuffix: ' will be permanently deleted.',
      uploaded: 'Uploaded {{num}} file.',
      uploadedPlural: 'Uploaded {{num}} files.',
      deleted: 'Deleted {{name}}.',
    },
    // DPAS file detail
    dpasDetail: {
      loadingPreview: 'Loading preview…',
      noPreview: 'No preview available.',
      noInlinePreview: 'No inline preview for this file type — use Download.',
      downloadResolution: 'Download resolution',
      original: 'Original',
      version: 'Version',
      latest: 'Latest',
      created: 'Created',
      modified: 'Modified',
      size: 'Size',
      delete: 'Delete',
      copied: 'Copied',
      copyLink: 'Copy link',
      linkCopied: 'Link copied to clipboard',
      linkCopyError: 'Could not copy link.',
      preparing: 'Preparing…',
      download: 'Download',
    },
    // DPAS config
    dpasConfig: {
      saved: 'Configuration saved.',
      loading: 'Loading configuration…',
      couldntLoad: "Couldn't load configuration: {{msg}}",
      orchestrationTriggered: 'Orchestration triggered.',
      startOrchestrator: 'Start orchestrator',
      lastNightlyRun: 'Last nightly run',
      success: 'Success',
      failed: 'Failed',
      images: '{{num}} images',
      colSearchKey: 'Search key',
      colError: 'Error',
      project: 'Project',
      csvFile: 'CSV file',
      contactEmails: 'Contact emails',
      remove: 'Remove',
      noContacts: 'No contacts.',
      addEmailPlaceholder: 'Add email…',
      addEmail: 'Add email',
      notValidEmail: 'Not a valid email address.',
      csvMapping: 'CSV mapping',
      firstRowHeader: '(first row is the header)',
      raw: 'Raw',
      table: 'Table',
      importFromFile: 'Import from file',
      removeRow: 'Remove row',
      noRows: 'No rows.',
      addRow: 'Add row',
      saving: 'Saving…',
      saveChanges: 'Save changes',
      startOrchestratorTitle: 'Start orchestrator',
      startOrchestratorConfirm:
        'Trigger the DPAS orchestration now? This processes the configured images on the live orchestration.',
      starting: 'Starting…',
      start: 'Start',
    },
  },
  translations: {
    de: () => import('./de/devsecops'),
  },
});
